<template>
  <v-dialog id="dialog" v-model="dialog" max-width="900px" :overlay-opacity="0.93">
    <template v-slot:activator="{ on }">
      <v-card class="ma-2" v-on="on" raised hover rounded>
        <v-hover v-slot="{ hover }">
          <v-img :src="template.imageUrl" class="white--text align-end" height="180px">

            <v-chip v-for="category in template.categories" :key="category" small class="mb-2 ml-2 white--text">{{
    category }}</v-chip>

            <v-fade-transition>
              <v-overlay v-if="hover" absolute class="text-subtitle-2 align-start" opacity="0.9">
                <div class="ma-2">
                  {{ template.description }}
                </div>

              </v-overlay>
            </v-fade-transition>
          </v-img>

        </v-hover>

        <v-card-title class="text-subtitle-2">{{ template.organization }} / {{ template.name }}</v-card-title>
      </v-card>
    </template>

    <v-card v-if="!computeSelection">
      <v-card-text>

        <v-row align="start" dense>
          <v-col class="d-flex mt-5" md="8" sm="12" xs="12">

            <v-row class="mt-2" align="start" dense>
              <v-col class="d-flex mb-2" md="12" sm="12" xs="12">
                <v-text-field dense v-model="template.name" label="Template" outlined disabled readonly></v-text-field>
              </v-col>

              <v-col class="mb-2" md="12" sm="12" xs="12">
                <div class="self-start">
                  <span v-if="template.categories">
                    <v-chip small v-for="category in template.categories" :key="category" class="mr-2">{{ category
                      }}</v-chip>
                  </span>


                  <v-chip small v-if="template.gpu === 'required'" class="primary">
                    Requires GPU
                  </v-chip>
                  <v-chip small v-else-if="template.gpu === 'optional'" class="secondary--text">
                    GPU Optional
                  </v-chip>
                </div>
              </v-col>

              <v-col class=" mb-2" md="12" sm="12" xs="12">
                <div class="self-start">
                  {{ template.description }}
                </div>
              </v-col>
            </v-row>


          </v-col>
          <v-col class="d-flex mt-5" md="4" sm="12" xs="12">
            <v-img :src="template.imageUrl" class="white--text align-end ml-3" height="180px">
            </v-img>
          </v-col>
        </v-row>

        <v-row v-if="usage" align="center" dense>
          <span class="ml-1 mb-2 text-h6 white--text">Usage</span>
        </v-row>

        <v-row align="center" dense>
          <p class="ml-1">
            {{ usage }}
          </p>
        </v-row>

        <div v-if="examples.length > 0">
          <div v-for="(example, i) in examples" :key="`example-${i}`" class="mt-2">
            <v-row v-if="example.title" align="center" dense>
              <span class="ml-1 mb-2 text-subtitle-1 white--text">{{ example.title }}</span>
            </v-row>

            <v-row v-if="example.description" align="center" dense>
              <p class="ml-1">
                {{ example.description }}
              </p>
            </v-row>

            <v-row v-if="example.code" align="center" dense>
              <v-col class="" md="12" sm="12" xs="12">
                <markup class="" language="bash" :code="example.code" :inline="false"></markup>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn small text v-on:click.native="dialog = false" class="secondary--text">Cancel</v-btn>
        <v-spacer></v-spacer>

        <v-btn v-if="authenticated" small text v-on:click="computeSelection = true"
          class="primary--text">Continue</v-btn>

        <v-btn v-else small text :href="`/login?redirectTo=/templates?id=${template.id}`" class="primary--text">Login to
          deploy</v-btn>
      </v-card-actions>

    </v-card>

    <v-card v-else>
      <v-card-title>
        Deployment settings
      </v-card-title>
      <v-card-text>

        <v-row align="center" dense>
          <v-col class="mt-5" cols="12" sm="12" md="12">
            <v-select no-data-text="You are not a member of any project" v-model="project" :items="projectMemberships"
              dense :disabled="projectMemberships.length === 1" item-text="project.name" item-value="project"
              label="Project" small outlined></v-select>
          </v-col>
        </v-row>

        <v-row align="center" dense>
          <v-col cols="12" sm="12" md="12">
            <v-select no-data-text="Project doesn't have any namespaces" v-model="namespace" :items="namespaces"
              :disabled="namespaces.length === 1" dense item-text="name" item-value="id" label="Namespace" small
              outlined></v-select>
          </v-col>
        </v-row>

        <div v-if="template.parameters && template.parameters.length > 0" class="mt-5 mb-5">
          <!-- <v-divider></v-divider> -->

          <v-row align="center" dense>
            <span class="ml-1 text-subtitle-1 white--text">Parameters</span>

            <v-spacer></v-spacer>
            <v-switch label="Show all" v-model="advancedParams"></v-switch>
          </v-row>

          <template v-for="(param, i) in params">
            <v-row :key="`param-${i}`" dense v-if="advancedParams || param.default === ''">
              <v-col md="4" sm="4" xs="4">
                <v-subheader style="padding-left:0px">{{ param.displayName }}</v-subheader>
              </v-col>

              <v-col sm="8">
                <v-text-field dense v-model="param.value" :readonly="param.readonly" :disabled="param.readonly"
                  :hint="param.description" persistent-hint label="" outlined></v-text-field>
              </v-col>
            </v-row>
          </template>
        </div>

        <div v-if="project">
          <v-row align="center" dense>
            <span class="ml-1 text-subtitle-1 white--text">Device selection</span>
          </v-row>

          <div v-if="devices.length === 0">
            <p class="mt-4" v-if="project">
              Please add at least one device to your project to deploy this
              application. To add a new device, visit the <router-link
                :to="`${project.id}/devices`">devices</router-link>
              page and click "provision device" button
              or run the following command on your device to add it to your project:
            </p>
            <markup class="mt-5" language="bash" :code="installCommand" :inline="false"></markup>
          </div>
          <div v-else>
            <AppDeviceSelector :devices="devices" :selectedDevices="scheduling.devices"
              @update:selectedDevices="scheduling.devices = $event" :selectorKey="scheduling.selector.key"
              @update:selectorKey="scheduling.selector.key = $event" :selectorValue="scheduling.selector.value"
              @update:selectorValue="scheduling.selector.value = $event">
            </AppDeviceSelector>


            <v-alert v-if="missingGpu" class="ml-1" type="info" dense outlined>
              <span v-if="scheduling.devices.length === 1">
                We are not detecting a GPU on the selected device while the template
                requires it. If you know you have a working GPU with an Nvidia container
                toolkit, ignore this message.
              </span>
              <span v-else>
                One or more selected devices don't have detected GPUs
                while the template requires for them.
              </span>
            </v-alert>
          </div>
        </div>


      </v-card-text>

      <!-- app create error -->
      <v-alert v-if="error" type="error" dismissible>
        {{ error }}
      </v-alert>

      <v-card-actions>
        <v-btn small text v-on:click.native="computeSelection = false" class="secondary--text">Back</v-btn>
        <v-spacer></v-spacer>
        <v-btn small text v-on:click="deploy()" :disabled="devices.length === 0 || !paramsValid" class="primary--text">Create</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import AppDeviceSelector from '@/components/helpers/AppDeviceSelector.vue';
import Markup from '../helpers/Markup';

import YAML from 'yaml';

export default {
  components: {
    AppDeviceSelector,
    Markup,
  },
  data() {
    return {
      timer: null,
      dialog: false,
      name: '', // app name
      params: [],
      advancedParams: false,

      computeSelection: false, // Select devices
      project: null,
      namespace: null,

      scheduling: {
        type: 'Conditional',
        devices: [],
        selector: {
          key: '',
          value: '',
        }
      },

      deployError: null
    }
  },

  props: {
    template: Object,
    open: Boolean
  },

  beforeDestroy() {
    clearInterval(this.timer)
  },

  beforeUnmount() {
    clearInterval(this.timer)
  },

  mounted() {
    // Copy values from template to params
    this.params = this.template.parameters.map(param => {
      return {
        name: param.name,
        // For display name, split CamelCase to words
        displayName: param.name.replace(/([A-Z])/g, ' $1').trim(),
        readonly: param.readonly,
        value: param.default,
        description: param.description,
        default: param.default,
      }
    })

    if (this.open) {
      this.dialog = true
    }
  },

  watch: {
    dialog(val) {
      // Clear params and devices on close
      if (val) {
        this.$router.push({
          query: {
            id: this.template.id
          }
        }).catch(() => { });
      } else {
        this.params.forEach(param => {
          param.value = param.default
        })
        this.scheduling.devices = []
        this.scheduling.selector.key = ''
        this.scheduling.selector.value = ''
        this.deployError = null

        this.$router.push({ query: {} }).catch(() => { });
      }
    },
    // Once project is loaded, load registration tokens
    async project() {
      clearInterval(this.timer)

      this.$store.dispatch('ListDeviceRegistrationTokens', {
        projectId: this.project.id
      })

      this.$store.dispatch('ListNamespaces', {
        project: this.project.id
      }).then(() => {
        this.namespace = this.namespaces[0]

        this.$store.dispatch('ListApplications', {
          projectId: this.project.id,
          namespaceId: this.namespace.id
        })
      })

      await this.fetchDevices()

      if (this.devices.length === 0) {
        // Start polling devices every 2 seconds
        this.timer = setInterval(() => {
          this.fetchDevices()
        }, 2000)
        return
      }
    },
    computeSelection() {
      // If user has only a single project membership, select it
      if (this.projectMemberships.length === 1) {
        this.project = this.projectMemberships[0].project
      }
    }
  },

  computed: {
    authenticated() {
      return this.$auth.check()
    },
    loading() {
      return this.$store.state.application.templatesLoading;
    },
    usage() {
      if (!this.template) {
        return ''
      }


      if (this.template.usage && this.template.usage !== '') {

        return this.template.usage
      }
      return ''
    },
    examples() {
      if (this.template.examples) {
        return this.template.examples
      }
      return []
    },
    error() {
      if (this.deployError) {
        return this.deployError
      }
      return this.$store.state.application.error
    },
    requiredParams() {
      return this.template.parameters.filter(param => param.default === '')
    },
    paramsValid() {
      // Check if all params have values set
      return this.params.every(param => param.value !== '');
    },
    projectMemberships() {
      return this.$store.state.project.memberships
    },
    registrationToken() {
      if (this.$store.state.device.registrationTokens.length === 0) return {}
      return this.$store.state.device.registrationTokens[0]
    },
    namespaces() {
      return this.$store.state.namespace.namespaces
    },
    devices() {
      return this.$store.state.device.devices
    },
    installCommand() {
      // some crappy formatting
      return `curl https://downloads.synpse.net/install.sh | \\\n  AGENT_PROJECT=${this.project?.id} \\\n  AGENT_REGISTRATION_TOKEN=${this.registrationToken?.id} \\\n  AGENT_CONTROLLER_URI=${window.location.protocol}//${window.location.host}/api \\\n  bash`
    },
    missingGpu() {
      if (this.template && this.template.gpu !== 'required') {
        return false
      }

      let missingGpu = false

      this.scheduling.devices.forEach(deviceId => {

        let device = this.devices.find(d => d.id === deviceId)

        if (device) {
          if (device?.info?.gpusInfo && device?.info?.gpusInfo?.length > 0) {
            // Check the GPUs, should have memory
            let validGpu = device.info.gpusInfo.some(gpu => gpu.memory !== "")
            if (!validGpu) {
              missingGpu = true
            }
          } else {
            missingGpu = true
          }
        }
      })

      return missingGpu
    },
    applications() {
      return this.$store.state.application.applications
    },
    createdApplication() {
      return this.$store.state.application.createdApplication
    },
  },

  methods: {
    async fetchDevices() {
      await this.$store.dispatch('GetDevices', {
        projectId: this.project.id,
        query: '',
        pageSize: 100,
        pageToken: '',
      })
    },
    async deploy() {
      var params = {}

      // Add this.template.parameters name and value to params
      // and ensure all param.value are strings (convert to string if not already)
      if (this.params && this.params.length > 0) {
        this.params.forEach(param => {
          params[param.name] = param.value.toString()
        })
      }

      var appYaml = ''

      try {
        let resp = await this.$http.post(`templates/${this.template.id}`, params)
        appYaml = resp.data
      } catch (error) {
        this.deployError = error
        return
      }

      // Once rendered, parse YAML and set the scheduling
      var application = {}

      try {
        application = YAML.parse(appYaml)
      }
      catch (err) {
        this.deployError = err
        return
      }

      // Go through application spec env and ensure all values
      // are strings (convert to string if not already)
      // Go through each container and ensure all values are strings
      application.spec.containers.forEach(container => {
        if (container.env && container.env.length > 0) {
          container.env.forEach(env => {
            env.value = env.value.toString()
          })
        }
      })

      application.scheduling = this.scheduling

      // Metadata
      application.projectId = this.project.id
      application.namespaceId = this.namespace.id
      application.templateId = this.template.id    

      application.name = this.getApplicationName(application)

      await this.$store.dispatch('CreateApplication', application)
      if (this.error) {
        // Displaying error
        return
      }
      this.$router.push(
        {
          name: 'applicationDetails',
          params: { project: application.projectId, namespace: application.namespaceId, application: this.createdApplication.id }
        })
    },
    getApplicationName(app) {
      // If there's already an application with this name, append 4 random characters
      if (this.applications.find(a => a.name === app.name)) {
        return app.name + '-' + Math.random().toString(36).substring(7)
      }

      return app.name
    },
  }
}

</script>
